import { combineReducers } from 'redux';
import { NotificationType } from '~/services/slackAPI';
import { SlackActionType, SlackLinkAction } from '../../actions/slackActions';
import { SlackChannel } from '../../types/resources';

interface SlackState {
  updatingSlackLink: boolean;
  updatingSlackChannelsConfiguration: {
    successfulRuns: boolean;
    failedRuns: boolean;
  };
  listingSlackChannels: boolean;
  channels: SlackChannel[];
  isLinked: boolean;
  slackChannelsConfiguration:
    | { successfulRunsChannelId?: string; failedRunsChannelId?: string }
    | undefined;
  slackChannelError: string | undefined;
}

export const initialState: SlackState = {
  updatingSlackChannelsConfiguration: {
    successfulRuns: false,
    failedRuns: false,
  },
  updatingSlackLink: false,
  listingSlackChannels: false,
  channels: [],
  isLinked: false,
  slackChannelsConfiguration: undefined,
  slackChannelError: undefined,
};

const updatingSlackLink = (state = false, action: SlackLinkAction) => {
  switch (action.type) {
    case SlackActionType.LINK_TO_SLACK:
    case SlackActionType.UNLINK_FROM_SLACK:
      return true;
    case SlackActionType.LINK_TO_SLACK_OK:
    case SlackActionType.UNLINK_FROM_SLACK_OK:
    case SlackActionType.LINK_TO_SLACK_ERROR:
    case SlackActionType.UNLINK_FROM_SLACK_ERROR:
      return false;
    default:
      return state;
  }
};

const updatingSlackChannelsConfiguration = (
  state = {
    successfulRuns: false,
    failedRuns: false,
  },
  action: SlackLinkAction,
): SlackState['updatingSlackChannelsConfiguration'] => {
  switch (action.type) {
    case SlackActionType.CHANGE_SLACK_CHANNELS_CONFIGURATION:
    case SlackActionType.REMOVE_SLACK_CHANNEL_FROM_CONFIGURATION:
      return {
        successfulRuns: action.notificationType === NotificationType.SUCCESSFUL_RUNS,
        failedRuns: action.notificationType === NotificationType.FAILED_RUNS,
      };
    case SlackActionType.CHANGE_SLACK_CHANNELS_CONFIGURATION_OK:
    case SlackActionType.CHANGE_SLACK_CHANNELS_CONFIGURATION_ERROR:
    case SlackActionType.REMOVE_SLACK_CHANNEL_FROM_CONFIGURATION_OK:
    case SlackActionType.REMOVE_SLACK_CHANNEL_FROM_CONFIGURATION_ERROR:
      return {
        successfulRuns: false,
        failedRuns: false,
      };
    default:
      return state;
  }
};

const listingSlackChannels = (state = false, action: SlackLinkAction) => {
  switch (action.type) {
    case SlackActionType.LIST_SLACK_CHANNELS:
    case SlackActionType.GET_SLACK_CHANNEL_INFO:
      return true;
    case SlackActionType.LIST_SLACK_CHANNELS_OK:
    case SlackActionType.LIST_SLACK_CHANNELS_ERROR:
    case SlackActionType.GET_SLACK_CHANNEL_INFO_OK:
    case SlackActionType.GET_SLACK_CHANNEL_INFO_ERROR:
      return false;
    default:
      return state;
  }
};

type WithName = { name: string };
const nameCompare = (a: WithName, b: WithName) => a.name.localeCompare(b.name);

const channels = (state: SlackChannel[] = [], action: SlackLinkAction) => {
  switch (action.type) {
    case SlackActionType.LIST_SLACK_CHANNELS:
      return [];
    case SlackActionType.LINK_TO_SLACK_OK:
    case SlackActionType.LIST_SLACK_CHANNELS_OK:
      return action.data?.channels.sort(nameCompare) || [];
    case SlackActionType.UNLINK_FROM_SLACK_OK:
    case SlackActionType.LIST_SLACK_CHANNELS_ERROR:
      return [];
    case SlackActionType.GET_SLACK_CHANNEL_INFO_OK: {
      const alreadyIncluded = state.find(({ id }) => action.data?.channel?.id === id) !== undefined;

      if (alreadyIncluded) {
        return state;
      }

      return action.data?.channel ? [...state, action.data.channel] : state;
    }
    default:
      return state;
  }
};

const isLinked = (state = false, action: SlackLinkAction) => {
  switch (action.type) {
    case SlackActionType.LIST_SLACK_CHANNELS:
      return false;
    case SlackActionType.LINK_TO_SLACK_OK:
      return true;
    case SlackActionType.GET_SLACK_CHANNEL_INFO_OK:
    case SlackActionType.LIST_SLACK_CHANNELS_OK:
      return action.data?.linked || false;
    case SlackActionType.UNLINK_FROM_SLACK_OK:
    case SlackActionType.LIST_SLACK_CHANNELS_ERROR:
      return false;
    default:
      return state;
  }
};

const slackChannelsConfiguration = (
  state = {},
  action: SlackLinkAction,
): SlackState['slackChannelsConfiguration'] => {
  switch (action.type) {
    case SlackActionType.CHANGE_SLACK_CHANNELS_CONFIGURATION:
      return {
        successfulRunsChannelId: action.successfulRunsChannelId,
        failedRunsChannelId: action.failedRunsChannelId,
      };
    case SlackActionType.CHANGE_SLACK_CHANNELS_CONFIGURATION_OK:
    case SlackActionType.GET_SLACK_CHANNELS_CONFIGURATION_OK:
      return {
        successfulRunsChannelId: action.data.successfulRunsChannelId,
        failedRunsChannelId: action.data.failedRunsChannelId,
      };
    case SlackActionType.REMOVE_SLACK_CHANNEL_FROM_CONFIGURATION_OK:
      return {
        successfulRunsChannelId: action.data.successfulRunsChannelId,
        failedRunsChannelId: action.data.failedRunsChannelId,
      };
    case SlackActionType.GET_SLACK_CHANNELS_CONFIGURATION_ERROR:
    default:
      return state;
  }
};

const slackChannelError = (state = {}, action: SlackLinkAction) => {
  switch (action.type) {
    case SlackActionType.CHANGE_SLACK_CHANNELS_CONFIGURATION:
      return {
        successfulRunsChannelId: action.successfulRunsChannelId,
        failedRunsChannelId: action.failedRunsChannelId,
      };
    case SlackActionType.CHANGE_SLACK_CHANNELS_CONFIGURATION_OK:
    case SlackActionType.GET_SLACK_CHANNELS_CONFIGURATION_OK:
      return action.data?.error || null;
    case SlackActionType.REMOVE_SLACK_CHANNEL_FROM_CONFIGURATION_OK:
      return {
        successfulRunsChannelId: action.data.successfulRunsChannelId,
        failedRunsChannelId: action.data.failedRunsChannelId,
      };
    case SlackActionType.GET_SLACK_CHANNELS_CONFIGURATION_ERROR:
    default:
      return state;
  }
};

export default combineReducers<SlackState>({
  updatingSlackLink,
  updatingSlackChannelsConfiguration,
  listingSlackChannels,
  channels,
  isLinked,
  slackChannelsConfiguration,
  slackChannelError,
});
